import { registerCustomControlProvider } from '../registry';
import { buildObjectActionsButtons } from './shared';
import { triggerStateChangedRequest } from '~core';
import { showSidebarTab } from '~src/sidebarLegacy';
import { toggleTextBorder } from '~core';
import { isSvgLike } from './svgFloating';

const isTextObject = (o) => o && (o.type === 'textbox' || o.type === 'i-text');

registerCustomControlProvider({
  id: 'selection-floating-controls',
  match: (object) =>
    object && object.type === 'activeSelection' && !isSvgLike(object),
  create: (manager, selection) => {
    const getItems = () =>
      (typeof selection.getObjects === 'function'
        ? selection.getObjects()
        : selection._objects) || [];

    const getTexts = () => getItems().filter(isTextObject);
    const allTexts = () => {
      const items = getItems();
      return items.length > 0 && items.every(isTextObject);
    };

    const applyToTexts = (fn) => {
      const texts = getTexts();
      if (!texts.length) return;
      texts.forEach((t) => {
        try {
          fn(t);
          t.set('dirty', true);
        } catch (e) {
          console.error('Failed applying batch text operation', e);
        }
      });
      if (window.canvas) window.canvas.requestRenderAll();
      try {
        triggerStateChangedRequest();
      } catch (e) {
        console.error('Failed triggering state change after text op', e);
      }
    };

    const toggleBold = () =>
      applyToTexts((t) => {
        const w = t.fontWeight || 'normal';
        const isBold = w === 'bold' || parseInt(w) >= 600;
        t.fontWeight = isBold ? 'normal' : 'bold';
      });
    const toggleItalic = () =>
      applyToTexts((t) => {
        const it = t.fontStyle || 'normal';
        t.fontStyle = it === 'italic' ? 'normal' : 'italic';
      });
    const toggleUnderline = () =>
      applyToTexts((t) => {
        const ul = !!t.underline;
        t.underline = !ul;
      });
    const changeFontSizeBy = (delta) =>
      applyToTexts((t) => {
        const fs = Math.max(2, Math.round((t.fontSize || 16) + delta));
        t.fontSize = fs;
      });
    const setAlign = (align) =>
      applyToTexts((t) => {
        t.textAlign = align;
      });

    const alignSelection = (mode) => {
      const items = getItems();
      if (items.length < 2 || !window.canvas) return;
      // Compute selection bounds in absolute canvas space
      const selRect = selection.getBoundingRect(true);
      const selLeft = selRect.left;
      const selCenterX = selRect.left + selRect.width / 2;
      const selRight = selRect.left + selRect.width;

      items.forEach((obj) => {
        try {
          const r = obj.getBoundingRect(true);
          const objCenterX = r.left + r.width / 2;
          let dx = 0;
          switch (mode) {
            case 'left':
              dx = selLeft - r.left;
              break;
            case 'center':
              dx = selCenterX - objCenterX;
              break;
            case 'right':
              dx = selRight - (r.left + r.width);
              break;
            default:
              break;
          }
          obj.set('left', (obj.left || 0) + dx);
          obj.setCoords();
        } catch (e) {
          console.error('Failed aligning selection object', e);
        }
      });
      window.canvas.requestRenderAll();
      try {
        triggerStateChangedRequest();
      } catch (e) {
        console.error('Failed requesting render or state change', e);
      }
    };

    const toGroup = () => {
      try {
        const group = selection.toGroup();
        if (group && window.canvas) {
          window.canvas.setActiveObject(group);
        }
        if (window.canvas) window.canvas.requestRenderAll();
        try {
          triggerStateChangedRequest();
        } catch (e) {
          console.error('Failed updating ensureSquares for selection', e);
        }
      } catch (e) {
        console.error('Group selection failed', e);
      }
    };

    const options = {
      offsetY: 0,
      offsetX: 0,
      gap: 12,
      // Make placement follow Text logic (prefer vertical top/bottom)
      preferVerticalPlacement: true,
      initialView: 'home',
      views: {
        home: {
          layout: 'row',
          buttons: (() => {
            const buttons = [];

            if (allTexts()) {
              // Batch text controls
              buttons.push(
                {
                  text: 'Font',
                  className: 'btn-text-control btn-text-font',
                  onClick: () => {
                    showSidebarTab('font');
                  },
                },
                {
                  svg: '/assets/svg/testo-dimensione-diminuisci.svg',
                  className: 'btn-text-control btn-text-size-minus',
                  onClick: () => changeFontSizeBy(-20),
                },
                { type: 'container', className: 'btn-text-size-value' },
                {
                  svg: '/assets/svg/testo-dimensione-aumenta.svg',
                  className: 'btn-text-control btn-text-size-plus',
                  onClick: () => changeFontSizeBy(20),
                },
                {
                  className: 'btn-text-control btn-text-color',
                  onClick: () => {
                    showSidebarTab('colori');
                  },
                },
                {
                  className: 'btn-text-control btn-text-border',
                  onClick: () => {
                    try {
                      toggleTextBorder();
                    } catch (e) {
                      console.error('Failed toggling text border', e);
                    }
                  },
                },
                {
                  svg: '/assets/svg/testo-grassetto.svg',
                  className: 'btn-text-control btn-text-bold',
                  onClick: () => toggleBold(),
                },
                {
                  svg: '/assets/svg/test-sottolineato.svg',
                  className: 'btn-text-control btn-text-underline',
                  onClick: () => toggleUnderline(),
                },
                {
                  svg: '/assets/svg/testo-corsivo.svg',
                  className: 'btn-text-control btn-text-italic',
                  onClick: () => toggleItalic(),
                },
                {
                  svg: '/assets/svg/allineamento-testo-sinistra.svg',
                  className:
                    'btn-text-control btn-text-align btn-text-align-left',
                  onClick: () => setAlign('left'),
                },
                {
                  svg: '/assets/svg/allineamento-testo-centro.svg',
                  className:
                    'btn-text-control btn-text-align btn-text-align-center',
                  onClick: () => setAlign('center'),
                },
                {
                  svg: '/assets/svg/allineamento-testo-destra.svg',
                  className:
                    'btn-text-control btn-text-align btn-text-align-right',
                  onClick: () => setAlign('right'),
                }
              );
            }

            // Three-dots menu always present; include grouping and alignment inside
            const actions = [];
            actions.push(
              {
                text: 'Raggruppa',
                svg: '/assets/svg/raggruppa.svg',
                className: 'btn-customize',
                onClick: () => toGroup(),
              },
              {
                text: 'Allinea elementi',
                svg: '/assets/svg/allinea-elementi.svg',
                className: 'btn-customize',
                children: [
                  {
                    text: 'Sinistra',
                    svg: '/assets/svg/allinea-sinistra.svg',
                    className: 'btn-customize',
                    onClick: () => alignSelection('left'),
                  },
                  {
                    text: 'Centro',
                    svg: '/assets/svg/allinea-centro.svg',
                    className: 'btn-customize',
                    onClick: () => alignSelection('center'),
                  },
                  {
                    text: 'Destra',
                    svg: '/assets/svg/allinea-destra.svg',
                    className: 'btn-customize',
                    onClick: () => alignSelection('right'),
                  },
                ],
              }
            );
            // Append generic object actions
            actions.push(...buildObjectActionsButtons(() => selection));

            buttons.push({
              inlineSvg:
                '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" fill-rule="evenodd" d="M5 14a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm7 0a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm7 0a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"></path></svg>',
              className: 'btn-customize',
              hideArrow: true,
              children: actions,
            });

            return buttons;
          })(),
        },
      },
    };

    const control = manager.createFloatingControls(selection, options);

    const ensureSquares = () => {
      const root = control && control.getElement ? control.getElement() : null;
      if (!root) return;
      const colorBtn = root.querySelector('.btn-text-color');
      if (colorBtn && !colorBtn.querySelector('.text-color-square')) {
        const sq = document.createElement('div');
        sq.className = 'text-square text-color-square';
        colorBtn.appendChild(sq);
      }
      const borderBtn = root.querySelector('.btn-text-border');
      if (borderBtn && !borderBtn.querySelector('.text-border-square')) {
        const sq = document.createElement('div');
        sq.className = 'text-square text-border-square';
        borderBtn.appendChild(sq);
      }
    };

    const updateActiveStates = () => {
      if (!allTexts()) return;
      const root = control && control.getElement ? control.getElement() : null;
      if (!root) return;
      ensureSquares();
      const texts = getTexts();
      // Font size value (show shared or mixed)
      try {
        const sizeEl = root.querySelector('.btn-text-size-value');
        if (sizeEl) {
          const sizes = texts.map((t) => Math.round(t.fontSize || 0));
          const allSame = sizes.every((s) => s === sizes[0]);
          sizeEl.textContent = allSame ? `${sizes[0]}` : '—';
        }
      } catch (e) {
        console.error('Failed updating size display (V spacing)', e);
      }

      // Bold / Italic / Underline active states (active only if all)
      const allBold = texts.every((t) => {
        const w = t.fontWeight || 'normal';
        return w === 'bold' || parseInt(w) >= 600;
      });
      const allItalic = texts.every(
        (t) => (t.fontStyle || 'normal') === 'italic'
      );
      const allUnderline = texts.every((t) => !!t.underline);
      const boldBtn = root.querySelector('.btn-text-bold');
      const italicBtn = root.querySelector('.btn-text-italic');
      const underlineBtn = root.querySelector('.btn-text-underline');
      if (boldBtn) boldBtn.classList.toggle('active', allBold);
      if (italicBtn) italicBtn.classList.toggle('active', allItalic);
      if (underlineBtn) underlineBtn.classList.toggle('active', allUnderline);

      // Text align
      const aligns = texts.map((t) => t.textAlign || 'left');
      const allSameAlign = aligns.every((a) => a === aligns[0]);
      const current = allSameAlign ? aligns[0] : '';
      const alignBtns = {
        left: root.querySelector('.btn-text-align-left'),
        center: root.querySelector('.btn-text-align-center'),
        right: root.querySelector('.btn-text-align-right'),
      };
      Object.entries(alignBtns).forEach(([key, el]) => {
        if (!el) return;
        el.classList.toggle('active', !!current && key === current);
      });

      // Color square
      try {
        const fills = Array.from(new Set(texts.map((t) => t.fill || '#000')));
        const colorSq = root.querySelector('.text-color-square');
        if (colorSq) {
          colorSq.style.backgroundColor =
            fills.length === 1 ? fills[0] : '#cbd5e1';
        }
      } catch (e) {
        console.error('Failed updating size display (H spacing)', e);
      }

      // Border square
      try {
        const strokes = Array.from(
          new Set(texts.map((t) => t.stroke || 'transparent'))
        );
        const borderSq = root.querySelector('.text-border-square');
        if (borderSq) {
          const active =
            strokes.length === 1 &&
            strokes[0] !== 'transparent' &&
            strokes[0] !== null;
          borderSq.style.borderColor = active ? strokes[0] : '#cbd5e1';
          borderSq.classList.toggle('active', active);
        }
      } catch (e) {
        console.error('Failed updating size display (opacity)', e);
      }

      // Font family button label (show actual name if all the same)
      try {
        const fonts = texts.map((t) => t.fontFamily || '');
        const allSameFont = fonts.every((f) => f === fonts[0]);
        const fontBtn = root.querySelector('.btn-text-font');
        if (fontBtn) {
          const idx = parseInt(
            fontBtn.getAttribute('data-button-index') || '0',
            10
          );
          control.updateButton(idx, {
            text: allSameFont && fonts[0] ? fonts[0] : 'Font',
          });
        }
      } catch (e) {
        void e;
      }

      if (typeof control.refreshIconColors === 'function') {
        control.refreshIconColors();
      }
    };

    // Initial and reactive updates
    setTimeout(updateActiveStates, 0);
    try {
      const update = () => setTimeout(updateActiveStates, 0);
      window.canvas.on('selection:created', update);
      window.canvas.on('selection:updated', update);
      window.canvas.on('object:modified', update);
      window.canvas.on('custom:state:changed', update);
    } catch (e) {
      console.error('Failed wiring selection update handlers', e);
    }

    return control;
  },
});
